import { useEffect, useState } from 'react'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { useAccountIdentity } from '@/hooks/use-account-identity'
import { useProfile } from '@/hooks/use-profile'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import {
  PersonIdentityFields,
  type PersonIdentityFieldsValue,
} from '@/components/account/person-identity-fields'
import { buildPersonIdentityDraftWithFallbacks } from '@/lib/person-identity-display'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'
import { userPersonNameFromAuth } from '@/lib/format-display-name'
import { useAuth } from '@/hooks/use-auth'

interface IdentityStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function IdentityStep({ onNext, onBack, isCompleting, canGoBack }: IdentityStepProps) {
  const { user } = useAuth()
  const { profile } = useProfile()
  const {
    identity,
    updateIdentity,
    uploadAvatar,
    deleteAvatar,
    isUploadingAvatar,
    isDeletingAvatar,
    uploadAvatarError,
    isUpdating,
  } = useAccountIdentity()

  const authName = user ? userPersonNameFromAuth(user) : ''
  const [draft, setDraft] = useState<PersonIdentityFieldsValue>(() =>
    buildPersonIdentityDraftWithFallbacks(identity, profile, authName),
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(buildPersonIdentityDraftWithFallbacks(identity, profile, authName))
  }, [identity?.name, identity?.greeting_name, profile, authName])

  const handleContinue = async () => {
    setSaving(true)
    setError(null)
    try {
      const saved = buildPersonIdentityDraftWithFallbacks(identity, profile, authName)
      if (draft.name !== saved.name || draft.greeting_name !== saved.greeting_name) {
        await updateIdentity({
          name: draft.name.trim(),
          greeting_name: draft.greeting_name.trim(),
        })
      }
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save identity')
    } finally {
      setSaving(false)
    }
  }

  const busy = isCompleting || saving || isUpdating || isUploadingAvatar

  return (
    <div className="space-y-6">
      <PersonIdentityFields
        layout="wizard"
        value={draft}
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        avatarUrl={identity?.avatar_url ?? profile?.avatar_url}
        avatarColorSeed={String(profile?.id ?? identity?.id ?? identity?.email ?? 'profile')}
        uploadAvatar={uploadAvatar}
        deleteAvatar={deleteAvatar}
        isUploadingAvatar={isUploadingAvatar}
        isDeletingAvatar={isDeletingAvatar}
        uploadAvatarError={uploadAvatarError}
        disabled={busy}
        nameDescription="Optional — you can add or change this anytime from your profile or account settings."
      />

      {error ? <div className="text-sm text-destructive">{error}</div> : null}

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleContinue}
        backDisabled={busy}
        continueDisabled={busy}
        isBusy={busy}
      />
    </div>
  )
}
